import { inject, Injectable } from '@angular/core';
import {
  browserLocalPersistence,
  onIdTokenChanged,
  sendPasswordResetEmail,
  setPersistence,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signOut,
  type Unsubscribe,
  type User,
  type UserCredential,
} from 'firebase/auth';
import { FIREBASE_AUTH } from './firebase-auth.provider';

@Injectable({ providedIn: 'root' })
export class FirebaseAuthClient {
  private readonly auth = inject(FIREBASE_AUTH);

  setLanguage(language: string): void {
    this.auth.languageCode = language;
  }

  initializePersistence(): Promise<void> {
    return setPersistence(this.auth, browserLocalPersistence);
  }

  whenAuthStateReady(): Promise<void> {
    return this.auth.authStateReady();
  }

  observe(listener: (user: User | null) => void): Unsubscribe {
    return onIdTokenChanged(this.auth, listener);
  }

  login(email: string, password: string): Promise<UserCredential> {
    return signInWithEmailAndPassword(this.auth, email, password);
  }

  loginWithCustomToken(token: string): Promise<UserCredential> {
    return signInWithCustomToken(this.auth, token);
  }

  logout(): Promise<void> {
    return signOut(this.auth);
  }

  resetPassword(email: string): Promise<void> {
    return sendPasswordResetEmail(this.auth, email);
  }

  idToken(): Promise<string | null> {
    return this.auth.currentUser?.getIdToken() ?? Promise.resolve(null);
  }

  /** Fetches a new ID token, so claims written since the last one take effect. */
  async refreshIdToken(): Promise<void> {
    await this.auth.currentUser?.getIdToken(true);
  }

  async claim(name: string): Promise<unknown> {
    const result = await this.auth.currentUser?.getIdTokenResult();
    return result?.claims[name];
  }
}
